import { SUMMER_SALE } from "@/lib/promo";

const SHOPIFY_API_VERSION = '2026-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'skin-style-studio-rklte.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = '9531d5cbc123db8ba8999c60a0eecc72';

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    /** Raw Shopify description (rich text); keeps the line breaks the seller typed. */
    descriptionHtml?: string;
    handle: string;
    productType?: string;
    tags?: string[];
    createdAt?: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          sku?: string | null;
          price: {
            amount: string;
            currencyCode: string;
          };
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

/**
 * Shopify stores product descriptions as rich text. Its plain-text
 * `description` field collapses the line breaks the seller typed, so we derive
 * readable text from `descriptionHtml` instead — every tag is stripped here, so
 * no markup can ever reach the page.
 */
export function htmlToPlainText(html?: string | null): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>(\r?\n)?/gi, "\n")
    .replace(/<\/(p|div|li|ul|ol|h[1-6]|tr|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 12_000);

  try {
    const response = await fetch(SHOPIFY_STOREFRONT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });

    if (response.status === 402) {
      throw new Error('Shopify billing is inactive.');
    }
    if (!response.ok) {
      throw new Error(`Shopify request failed (HTTP ${response.status}).`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(`Shopify error: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
    }
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Shopify request timed out. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export const STOREFRONT_PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          descriptionHtml
          handle
          productType
          tags
          createdAt
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
              title
              sku
              price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

export const STOREFRONT_PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      productType
      tags
      createdAt
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 20) {
        edges {
          node {
            id
              title
              sku
              price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

// Cart mutations
export const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      checkoutUrl
    }
  }
`;

export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } attributes { key value } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        totalQuantity
        checkoutUrl
        discountCodes { code applicable }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } attributes { key value } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

/**
 * Shopify's Cart API returns the only supported checkout URL. Keep it intact:
 * adding storefront channel parameters can route a headless cart to the Online
 * Store theme and produce a 404. We only validate the destination before
 * navigating so a malformed API response can never become an open redirect.
 */
export function validateCheckoutUrl(checkoutUrl: string): string | null {
  try {
    const url = new URL(checkoutUrl);
    const hostname = url.hostname.toLowerCase();
    // Shopify returns the checkout on the store's primary domain (e.g.
    // skinvault.be), on a checkout.* subdomain, or on *.myshopify.com.
    const storeDomains = [
      'skinvault.be',
      'skin-vault.fr',
      'skin-vault.info',
      'skinvault.fr',
    ];
    const isShopifyCheckout =
      hostname === 'checkout.shopify.com' ||
      hostname === 'shop.app' ||
      hostname.endsWith('.myshopify.com') ||
      storeDomains.some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );

    if (url.protocol !== 'https:' || !isShopifyCheckout) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(e => e.message.toLowerCase().includes('cart not found') || e.message.toLowerCase().includes('does not exist'));
}

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: { amount: string; currencyCode: string };
  quantity: number;
  selectedOptions: Array<{ name: string; value: string }>;
  /**
   * Shopify cart line attributes. Sent as `attributes` on cartLinesAdd /
   * cartCreate so they flow into the final order (visible to the merchant).
   * Used for model selection on hydrogel skins, etc.
   */
  attributes?: Array<{ key: string; value: string }>;
}

export async function createShopifyCart(item: CartItem): Promise<{ cartId: string; checkoutUrl: string | null; lineId: string }> {
  // Keep cart creation independent from promotions. Some discount codes can be
  // rejected during cartCreate, which previously made the UI silently discard
  // an otherwise valid cart. The active code is applied in prepareCheckout.
  const data = await storefrontApiRequest(CART_CREATE_MUTATION, {
    input: {
      lines: [{
        quantity: item.quantity,
        merchandiseId: item.variantId,
        ...(item.attributes && item.attributes.length > 0 ? { attributes: item.attributes } : {}),
      }],
    },
  });

  const payload = data?.data?.cartCreate;
  const userErrors = payload?.userErrors || [];
  if (userErrors.length > 0) {
    throw new Error(userErrors.map((error: { message: string }) => error.message).join(', '));
  }

  const cart = payload?.cart;
  if (!cart?.id) throw new Error('Shopify did not create a cart.');
  const lineId = cart.lines?.edges?.[0]?.node?.id;
  if (!lineId) throw new Error('Shopify did not add the selected product.');

  // Checkout is refreshed and validated again immediately before redirecting,
  // so an unusual checkout hostname must not make the visible cart disappear.
  const checkoutUrl = cart.checkoutUrl ? validateCheckoutUrl(cart.checkoutUrl) : null;
  return { cartId: cart.id, checkoutUrl, lineId };
}

export async function prepareShopifyCheckout(cartId: string): Promise<{ checkoutUrl: string; totalQuantity: number } | null> {
  const data = SUMMER_SALE.active
    ? await storefrontApiRequest(CART_DISCOUNT_CODES_UPDATE_MUTATION, {
        cartId,
        discountCodes: [SUMMER_SALE.code],
      })
    : await storefrontApiRequest(CART_QUERY, { id: cartId });

  const payload = SUMMER_SALE.active ? data?.data?.cartDiscountCodesUpdate : null;
  const userErrors = payload?.userErrors || [];
  if (userErrors.length > 0) {
    throw new Error(userErrors.map((error: { message: string }) => error.message).join(', '));
  }

  const cart = SUMMER_SALE.active ? payload?.cart : data?.data?.cart;
  const checkoutUrl = cart?.checkoutUrl ? validateCheckoutUrl(cart.checkoutUrl) : null;
  if (!checkoutUrl || !cart?.totalQuantity) return null;
  return { checkoutUrl, totalQuantity: cart.totalQuantity };
}

export async function addLineToShopifyCart(cartId: string, item: CartItem): Promise<{ success: boolean; lineId?: string; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{
      quantity: item.quantity,
      merchandiseId: item.variantId,
      ...(item.attributes && item.attributes.length > 0 ? { attributes: item.attributes } : {}),
    }],
  });
  const userErrors = data?.data?.cartLinesAdd?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };
  const lines = data?.data?.cartLinesAdd?.cart?.lines?.edges || [];
  // Match by variant + attributes so two lines of the same variant with
  // different model attributes don't collide.
  const targetAttrs = JSON.stringify((item.attributes || []).map(a => [a.key, a.value]).sort());
  const newLine =
    lines.find((l: { node: { id: string; merchandise: { id: string }; attributes?: Array<{ key: string; value: string }> } }) =>
      l.node.merchandise.id === item.variantId &&
      JSON.stringify((l.node.attributes || []).map(a => [a.key, a.value]).sort()) === targetAttrs
    ) || lines.find((l: { node: { id: string; merchandise: { id: string } } }) => l.node.merchandise.id === item.variantId);
  return { success: true, lineId: newLine?.node?.id };
}

export async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_UPDATE_MUTATION, { cartId, lines: [{ id: lineId, quantity }] });
  const userErrors = data?.data?.cartLinesUpdate?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };
  return { success: true };
}

export async function removeLineFromShopifyCart(cartId: string, lineId: string): Promise<{ success: boolean; cartNotFound?: boolean }> {
  const data = await storefrontApiRequest(CART_LINES_REMOVE_MUTATION, { cartId, lineIds: [lineId] });
  const userErrors = data?.data?.cartLinesRemove?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { success: false, cartNotFound: true };
  if (userErrors.length > 0) return { success: false };
  return { success: true };
}
